import Button from '@jetbrains/ring-ui/components/button/button'
import ButtonSet from '@jetbrains/ring-ui/components/button-set/button-set'
import {H1} from '@jetbrains/ring-ui/components/heading/heading'
import classNames from 'classnames'
import {memo} from 'react'

import OverviewBreadcrumbs from '../../../../containers/OverviewBreadcrumbs'
import {useBuildStatusIcon} from '../../../../hooks/useBuildStatusIcon'
import {useBuildStatusTooltip} from '../../../../hooks/useBuildStatusTooltip'
import {useBuildTitle} from '../../../../hooks/useBuildTitle'
import useDocumentTitle from '../../../../hooks/useDocumentTitle'
import {PlaceIdList} from '../../../../plugins/plugins.types'
import {stringifyId} from '../../../../types'
import BuildBranch from '../../../common/BranchLabel/BuildBranchLabel.container'
import BuildActionsDropdown from '../../../common/BuildActionsDropdown/BuildActionsDropdown'
import Description from '../../../common/Description/Description.container'
import EditEntity from '../../../common/EditEntity/EditEntity.container'
import Icon from '../../../common/Icon/Icon'
import BuildTypeInvestigations from '../../../common/Investigations/BuildTypeInvestigations'
import Plugin from '../../../common/Plugin/Plugin'
import RunBuild from '../../../common/RunBuild/RunBuild.container'
import SequenceLoader from '../../../common/SequenceLoader/SequenceLoader'
import StarBuild from '../../../common/StarBuild/StarBuild.container'
import StopBuild from '../../../common/StopBuild/StopBuild.container'
import {useIsCancellable} from '../../../common/StopBuild/StopBuild.hooks'
import {getIsInterrupted} from '../../../common/StopBuild/StopBuild.selectors'
import {StopButtonType} from '../../../common/StopBuild/StopBuild.types'

import BuildDetailsPopup from './BuildDetailsPopup/BuildDetailsPopup'
import type {Props} from './BuildPageHeader.types'
import BuildStatusChange from './BuildStatusChange/BuildStatusChange'
import BuildTypeInfoPopup from './BuildTypeInfoPopup/BuildTypeInfoPopup.container'

import styles from './BuildPageHeader.css'

import {useAppSelector} from 'src/hooks/react-redux'

const actionsAnchor = <Button dropdown>{`Actions`}</Button>

function BuildPageHeader({
  buildTypeName,
  buildId,
  buildTypeId,
  projectId,
  status,
  branch,
  state,
  personal,
}: Props) {
  const statusTooltip = useBuildStatusTooltip(buildId)
  const statusIcon = useBuildStatusIcon(buildId)
  const title = useBuildTitle(buildId)

  const isQueued = state === 'queued'
  const isRunning = state === 'running'

  const isFailure = status === 'FAILURE'
  const isSuccess = status === 'SUCCESS'

  useDocumentTitle(`${buildTypeName} / ${title} — TeamCity`)

  const isCancellable = useIsCancellable(buildId)
  const showStopButton = useAppSelector(
    reduxState =>
      (isRunning || isQueued) && (isCancellable || getIsInterrupted(reduxState, buildId)),
  )

  const titleClassNames = classNames(styles.title, {
    [styles.error]: isFailure,
    [styles.success]: isSuccess,
    [styles.queued]: isQueued,
  })
  const statusIconClassNames = classNames(styles.statusIcon, {
    [styles.queuedIcon]: isQueued && personal === false,
    [styles.pesronalQueuedIcon]: isQueued && personal === true,
  })

  return projectId != null && buildTypeId != null && buildId != null ? (
    <div className={styles.header}>
      <div className={styles.links}>
        <OverviewBreadcrumbs
          className={styles.breadcrumbs}
          linkClassName={styles.breadcrumbsLink}
        />
        <SequenceLoader className={styles.linksSequenceLoader} loader="">
          <RunBuild
            seqKey="RunBuild"
            buildTypeId={buildTypeId}
            withVisibilityDetect={false}
            branch={branch}
            redirectToQueuedBuild
            isPrimary
          />
          <EditEntity seqKey="EditEntity" />
        </SequenceLoader>
      </div>
      <div className={styles.main}>
        <div className={styles.leftColumn}>
          <H1 className={styles.heading}>
            <Icon
              name={statusIcon}
              className={statusIconClassNames}
              title={statusTooltip}
              size="M"
            />
            <span className={titleClassNames}>{title}</span>
            <StarBuild className={styles.star} buildId={buildId} showEmpty />
          </H1>
          <div className={styles.headerInfo}>
            <Description className={styles.description} lines={2} buildId={buildId} />
            <BuildStatusChange className={styles.statusChangeComment} buildId={buildId} />
          </div>
          <div className={styles.subHeader}>
            <BuildBranch className={styles.branch} buildId={buildId} withIcon />
            <ButtonSet className={styles.actions}>
              {showStopButton && (
                <StopBuild buildId={buildId} buttonType={StopButtonType.DEFAULT_BUTTON} />
              )}
              <BuildActionsDropdown key={state} anchor={actionsAnchor} />
              <BuildDetailsPopup buildId={buildId} />
            </ButtonSet>
            {!isQueued && !(isSuccess && isRunning) && (
              <BuildTypeInvestigations className={styles.investigation} buildTypeId={buildTypeId} />
            )}
          </div>
        </div>
        <div className={styles.rightColumn}>
          <BuildTypeInfoPopup
            key={stringifyId(buildTypeId)}
            buildTypeId={buildTypeId}
            buildId={buildId}
          />
        </div>
      </div>
      <Plugin placeId={PlaceIdList.SAKURA_BEFORE_CONTENT} />
    </div>
  ) : null
}
export default memo(BuildPageHeader)
